<template>
  <div v-if="stats" class="emp-overview" :class="`emp-overview--${section}`">
    <!-- section="metrics": sits on the page-title row -->
    <div v-if="section === 'metrics'" class="emp-metrics">
      <div class="emp-metric">
        <div class="emp-metric__value">{{ totalEmployees }}</div>
        <div class="emp-metric__label">
          {{ $t("employee.statistics.title") }}
        </div>
      </div>

      <div v-for="metric in roleMetrics" :key="metric.key" class="emp-metric">
        <div
          class="emp-metric__value"
          :class="`emp-metric__value--${metric.color}`"
        >
          {{ metric.percent }}%
        </div>
        <div class="emp-metric__label">{{ metric.label }}</div>
      </div>
    </div>

    <!-- section="tags": sits on the tabs row -->
    <div v-else class="emp-secondary">
      <div class="emp-tags">
        <v-chip
          v-for="role in customRoleStats"
          :key="role._id"
          x-small
          outlined
          class="emp-tag"
        >
          <v-icon left x-small>{{ role.icon }}</v-icon>
          {{ role.name }}
          <span class="ml-1">({{ role.count }})</span>
        </v-chip>
      </div>

      <div v-if="canSwitchWorkplace" class="emp-workplace">
        <v-btn-toggle
          :value="currentWorkplaceName"
          mandatory
          dense
          rounded
          color="primary"
          @change="setWorkplace($event)"
        >
          <v-btn small value="Office" class="emp-workplace__btn">
            <v-icon small left>business</v-icon>
            {{ $t("employee.officeWorkplace") }}
          </v-btn>
          <v-btn small value="Workshop" class="emp-workplace__btn">
            <v-icon small left>build</v-icon>
            {{ $t("employee.workshopWorkplace") }}
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "employeeOverview",
  props: {
    section: { type: String, default: "metrics" },
  },
  data: () => ({
    stats: null,
    currentWorkplace: null,
  }),
  created() {
    this.fetchStats();
    this.fetchWorkplace();
    this.$root.$on("employee-stats-refresh", this.fetchStats);
    this.$root.$on("employee-workplace-changed", this.fetchWorkplace);
  },
  beforeDestroy() {
    this.$root.$off("employee-stats-refresh", this.fetchStats);
    this.$root.$off("employee-workplace-changed", this.fetchWorkplace);
  },
  computed: {
    currentWorkplaceName() {
      return this.currentWorkplace ? this.currentWorkplace.workplace : null;
    },
    canSwitchWorkplace() {
      return !!this.currentWorkplace && this.canCreateHere;
    },
    totalEmployees() {
      return this.stats ? this.stats.totalEmployees || 0 : 0;
    },
    safetyOfficerCount() {
      return this.stats ? this.stats.safetyOfficerCount || 0 : 0;
    },
    fireMarshalCount() {
      return this.stats ? this.stats.fireMarshalCount || 0 : 0;
    },
    firstAiderCount() {
      return this.stats ? this.stats.firstAiderCount || 0 : 0;
    },
    customRoleStats() {
      return this.stats && Array.isArray(this.stats.customRoleStats)
        ? this.stats.customRoleStats
        : [];
    },
    requiredPercentageForRoles() {
      return this.currentWorkplaceName === "Workshop" ? 10 : 5;
    },
    safetyOfficerPercent() {
      if (this.totalEmployees < 50) return 100;
      return this.safetyOfficerCount >= 1 ? 100 : 0;
    },
    safetyOfficerColor() {
      if (this.totalEmployees < 50) return "ok";
      return this.safetyOfficerCount >= 1 ? "ok" : "bad";
    },
    fireMarshalPercent() {
      if (!this.totalEmployees) return 0;
      return Math.floor((this.fireMarshalCount / this.totalEmployees) * 100);
    },
    firstAiderPercent() {
      if (!this.totalEmployees) return 0;
      return Math.floor((this.firstAiderCount / this.totalEmployees) * 100);
    },
    roleMetrics() {
      const min = this.requiredPercentageForRoles;
      return [
        {
          key: "safetyOfficer",
          label: this.$t("employee.safetyOfficer"),
          percent: this.safetyOfficerPercent,
          color: this.safetyOfficerColor,
        },
        {
          key: "fireMarshal",
          label: this.$t("employee.fireMarshal"),
          percent: this.fireMarshalPercent,
          color: this.fireMarshalPercent >= min ? "ok" : "bad",
        },
        {
          key: "firstAider",
          label: this.$t("employee.firstAider"),
          percent: this.firstAiderPercent,
          color: this.firstAiderPercent >= min ? "ok" : "bad",
        },
      ];
    },
  },
  methods: {
    async fetchStats() {
      try {
        this.stats = await this.$store.dispatch("getEmployeeStats");
      } catch (e) {
        this.stats = null;
      }
    },
    async fetchWorkplace() {
      if (!this.canCreateHere) return;
      try {
        const workplace = await this.$store.dispatch("getWorkplace");
        this.currentWorkplace = workplace || { workplace: "Office" };
      } catch (e) {
        this.currentWorkplace = { workplace: "Office" };
      }
    },
    async setWorkplace(workplace) {
      if (
        !this.canCreateHere ||
        !this.currentWorkplace ||
        !workplace ||
        workplace === this.currentWorkplaceName
      )
        return;

      if (this.currentWorkplace && this.currentWorkplace._id) {
        await this.$store.dispatch("updateWorkplace", {
          id: this.currentWorkplace._id,
          ...this.currentWorkplace,
          workplace,
        });
      } else {
        await this.$store.dispatch("createWorkplace", { workplace });
      }

      const updated = await this.$store.dispatch("getWorkplace");
      this.currentWorkplace = updated || { workplace };
      this.$root.$emit("employee-workplace-changed");
    },
  },
};
</script>

<style scoped>
.emp-overview {
  display: flex;
  align-items: center;
}

.emp-metrics {
  display: flex;
  align-items: center;
}

.emp-metric {
  padding: 0 18px;
  text-align: right;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}
.emp-metric:first-child {
  border-left: 0;
}
.emp-metric:last-child {
  padding-right: 0;
}

.emp-metric__value {
  font-size: 19px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.emp-metric__value--ok {
  color: #2e7d32;
}
.emp-metric__value--bad {
  color: #c62828;
}

.emp-metric__label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
  white-space: nowrap;
}

.emp-secondary {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.emp-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.emp-workplace__btn {
  text-transform: none;
  letter-spacing: 0;
}
</style>
